import './ServiceSection.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Divider, message, Radio, Row, Select } from 'antd';
import Form from 'antd/es/form';
import { useTranslation } from 'react-i18next';
import DefaultAddress from './DefaultAddress';
import {
  fetchAddress,
  fetchPickupMethodsItems,
  fetchPickupSchedule,
  fetchServiceType,
} from 'services/orderService';
import { CommonOption } from 'types/common';
import {
  AddressItem,
  CollectionSchedule,
  OrderServiceType,
  PickupMethod,
} from 'types/dto/order';
import AddressManagement from '../addressManagement/AddressManagement';
import addNewAddressIcon from 'assets/images/plus.png';
import DrawerFormContainer from 'components/drawerFormContainer/DrawerFormContainer';
import NewAddress from '../addressManagement/NewAddress';
import { formatDateWithDay, formatTime } from 'utils/commonUtils';
import dayjs from 'dayjs';
import _ from 'lodash';
import {
  BACKEND_ISO_FORMAT,
  DATE_WITH_DAY_MM_DD_FORMAT,
  TIME_FORMAT,
} from 'constants/consts';
import { useSession } from 'context/SessionContext';
import { useOutletContext } from 'react-router-dom';
import { MemberProfile } from 'types/dto/profile';

interface ServiceSectionProps {
  isReadonly?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onLoadError?: (error: boolean) => void;
}

const ServiceSection: React.FC<ServiceSectionProps> = ({
  isReadonly = false,
  onLoadingChange,
  onLoadError,
}) => {
  const { t, i18n } = useTranslation('new_order');
  const lang = i18n.language;
  const isEnglish = lang === 'en';

  const [isAddressDrawerVisible, setIsAddressDrawerVisible] =
    useState<boolean>(false);
  const [serviceOptions, setServiceOptions] = useState<CommonOption[]>([]);
  const [pickupMethods, setPickupMethods] = useState<PickupMethod[]>([]);
  const [addressArray, setAddressArray] = useState<AddressItem[]>([]);
  const [selectedAddressIdByUser, setSelectedAddressIdByUser] = useState<
    number | null
  >(null);
  const { profile } = useOutletContext<{ profile: MemberProfile | null }>();
  const { session } = useSession();

  //collection address
  const collectionAddress = useMemo<AddressItem | undefined>(() => {
    if (selectedAddressIdByUser) {
      return addressArray.find(
        (addr) => addr.addressID === selectedAddressIdByUser
      );
    } else {
      //if addressArray is empty,this funciton will return null,
      //which keeps collectionAddress the same and won't cause useEffect to be called.
      //so we need to return an empty object to keep useEffect being called.
      return addressArray.find((addr) => addr.seq === 1) || addressArray?.[0] || {};
    }
  }, [addressArray, selectedAddressIdByUser]);

  const [isNewAddressVisible, setIsNewAddressVisible] =
    useState<boolean>(false);

  const [collectionSchedules, setCollectionSchedules] = useState<
    CollectionSchedule[]
  >([]);

  // Options of collection date and time.
  //
  // No day-of-week filtering here. getpickupschedule already returns only bookable slots
  // for the requested locationCode: dbo.cus_pickup_schedule_select filters sclocsch on
  // status 'A', the validity window (sch_strdtm/sch_endtm), lead time
  // (sch_disdaymin/sch_disdaymax), remaining capacity against orpichdr, and the location
  // via loc_exists(sch_loccde) minus loc_exists(sch_loccdeexc).
  //
  // A previous version re-filtered by a hardcoded per-district day table. It was removed
  // rather than corrected, for two reasons:
  //
  //  1. It cannot express the real rules. sch_loccdeexc excludes individual sub-locations
  //     from individual slots, so NT-SK-TKO gets Tuesday PM only while NT-ISL-TC gets
  //     Tuesday AM only, and HKI-CW-CEN/ML get AM only on every day. A day-of-week table
  //     has no way to say that, and would offer slots the backend then rejects.
  //  2. It drifts. sclocsch is maintained through Adm_Schedule_Save; rows 39-40
  //     (THU KL,NT) were added in 2024, over a year after the rest, and no hardcoded
  //     table would have picked them up.
  const collectionOptions = useMemo(() => {
    return collectionSchedules.map((schedule) => {
      return {
        value: schedule.id,
        label: `${formatDateWithDay(schedule.pickupDateString)}  ${formatTime(
          schedule.pickupStartTime
        )}-${formatTime(schedule.pickupEndTime)}`,
      };
    });
  }, [collectionSchedules]);

  const form = Form.useFormInstance();
  const serviceType: OrderServiceType = Form.useWatch('serviceTypeCode', form);
  const collectionDateAndTimeslot: number = Form.useWatch(
    'collectionDateAndTimeslot',
    form
  );

  const calculateDeliveryDatetime = (
    pickupDateString: string,
    serviceType: OrderServiceType
  ) => {
    let deliveryDate = dayjs(pickupDateString);

    switch (serviceType) {
      case 'N': {
        deliveryDate = deliveryDate.add(7, 'day');
        break;
      }
      case 'E': {
        deliveryDate = deliveryDate.add(3, 'day');
        break;
      }
      case 'S': {
        deliveryDate = dayjs('2099-12-31');
        break;
      }
      default: {
        deliveryDate = deliveryDate.add(7, 'day');
        break;
      }
    }

    return deliveryDate;
  };


  const deliveryDateAndTimeslot: string = useMemo(() => {

    if (serviceType === 'S') {
      return t('deliveryDateStorageServiceTip');

    }


    if (collectionDateAndTimeslot >= 0) {
      const scheduleItem = collectionSchedules.find(
        (item) => item.id === collectionDateAndTimeslot
      );

      if (scheduleItem) {
        const deliveryStartTime = calculateDeliveryDatetime(
          scheduleItem.pickupStartTime,
          serviceType
        );
        const deliveryEndTime = calculateDeliveryDatetime(
          scheduleItem.pickupEndTime,
          serviceType
        );

        return `${deliveryStartTime.format(
          DATE_WITH_DAY_MM_DD_FORMAT
        )}  ${deliveryStartTime.format(TIME_FORMAT)}-${deliveryEndTime.format(
          TIME_FORMAT
        )}`;
      }
    }
    return '-';
  }, [collectionDateAndTimeslot, serviceType, collectionSchedules]);

  const fetchPickupScheduleByDistrictCode = useCallback(
    async (districtCode: string) => {
      try {
        const data = await fetchPickupSchedule('/customer', districtCode);

        // Use functional update to avoid dependency on collectionSchedules
        setCollectionSchedules((prevSchedules) => {
          if (!_.isEqual(data, prevSchedules)) {
            return data;
          }
          return prevSchedules;
        });
      } catch (error) {
        console.error('Failed to fetch pickup schedule:', error);
      } finally {
        //last network request
        onLoadingChange?.(false);
      }
    },
    [onLoadingChange]
  );

  const getAddressFromServer = useCallback(async () => {
    try {
      // Fetch address
      const addressData: AddressItem[] = await fetchAddress('/customer', {
        juicyAppUniqueId: profile?.loyalty_account?.unique_id ?? '',
        lang: lang,
      });

      // Use functional update to avoid dependency on addressArray
      setAddressArray((prevAddressArray) => {
        if (!_.isEqual(addressData, prevAddressArray)) {
          return addressData || [];
        }
        return prevAddressArray;
      });
    } catch (error) {
      message.error(t('fetchAddressError'));
    }
  }, [lang, profile?.loyalty_account?.unique_id, session?._tk, t]);

  const handleEditAddress = () => {
    setIsAddressDrawerVisible(true);
  };

  const handleAddAddress = () => {
    setIsNewAddressVisible(true);
  };

  const handleAddressDrawerClose = () => {
    setIsAddressDrawerVisible(false);
  };

  const handleNewAddressClose = () => {
    setIsNewAddressVisible(false);
  };

  const handleNewAddressSave = async () => {
    setIsNewAddressVisible(false);
    getAddressFromServer();
  };

  const handleAddressSelect = async (selectedAddressId: number) => {
    if (selectedAddressId !== collectionAddress?.addressID) {
      setSelectedAddressIdByUser(selectedAddressId);
      await getAddressFromServer();
    }
    setIsAddressDrawerVisible(false);
  };

  // Initialize data on mount and when lang changes
  useEffect(() => {
    const initData = async () => {
      try {
        // Start loading
        onLoadingChange?.(true);
        onLoadError?.(false);

        //1. Fetch service type
        const responseData = await fetchServiceType('/customer', lang);

        let options: CommonOption[] = _.isPlainObject(responseData)
          ? _(responseData as Record<string, unknown>)
            .toPairs()
            .map(([k, v]) => ({
              value: k,
              label: (v as string).trim(),
            }))
            .value()
          : [];

        // Sort by value (key) in ascending order
        options = options.sort((a, b) => a.value.localeCompare(b.value));

        setServiceOptions(options);

        //2. Fetch pickup methods
        const pickupMethodsData = await fetchPickupMethodsItems(
          '/common',
          lang
        );

        setPickupMethods(pickupMethodsData || []);

        //3. Fetch address
        // Note: This will trigger another useEffect to fetch pickup schedules
        // We'll mark loading as complete in a separate useEffect when collectionSchedules are ready
        const addressData: AddressItem[] = await fetchAddress('/customer', {
          juicyAppUniqueId: profile?.loyalty_account?.unique_id ?? '',
          lang: lang
        });
        setAddressArray(addressData || []);

      } catch (err) {
        console.error('Failed to obtain data:', err);
        onLoadError?.(true);
        onLoadingChange?.(false);
      }
    };
    initData();
  }, [lang, onLoadingChange, onLoadError, t, profile?.loyalty_account?.unique_id]);

  //fetch pickup schedule when collection address changes
  //reset selected address id when collectionAddress changes
  useEffect(() => {
    //clear selected schedule
    form.setFieldValue('collectionDateAndTimeslot', null);
    if (collectionAddress?.discde) {
      //Fetch pickup schedule by district code
      fetchPickupScheduleByDistrictCode(collectionAddress.discde);
    } else {
      //no need to call fetchPickupScheduleByDistrictCode, so we can mark loading as complete
      onLoadingChange?.(false);
    }

    if (collectionAddress?.addressID) {
      //calling the following codes will cause [circular references Error warning], But it won't break the app.
      //I think it's a bug of antd form.
      form.setFieldValue('colAddressId', collectionAddress.addressID);
      form.setFieldValue('delAddressId', collectionAddress.addressID);
    }

  }, [collectionAddress, fetchPickupScheduleByDistrictCode, form, onLoadingChange]);

  // Update form time fields when collectionDateAndTimeslot changes
  useEffect(() => {
    if (collectionDateAndTimeslot >= 0) {
      const scheduleItem = collectionSchedules.find(
        (item) => item.id === collectionDateAndTimeslot
      );
      if (scheduleItem) {
        // Set collection times (ISO UTC format)
        form.setFieldValue('colStartTime', scheduleItem.pickupStartTime);
        form.setFieldValue('colEndTime', scheduleItem.pickupEndTime);

        // Calculate and set delivery times
        const deliveryStartTime = calculateDeliveryDatetime(
          scheduleItem.pickupStartTime,
          serviceType
        );
        const deliveryEndTime = calculateDeliveryDatetime(
          scheduleItem.pickupEndTime,
          serviceType
        );

        // Set delivery times (ISO UTC format)
        form.setFieldValue(
          'delStartTime',
          deliveryStartTime.format(BACKEND_ISO_FORMAT)
        );
        form.setFieldValue(
          'delEndTime',
          deliveryEndTime.format(BACKEND_ISO_FORMAT)
        );
      }
    }
  }, [collectionDateAndTimeslot, collectionSchedules, serviceType, form]);

  const matchedAddress = addressArray.find(
    (addr) => addr.addressID === collectionAddress?.addressID,
  );
  let displayAddress = '';
  if (matchedAddress) {
    const lodName = isEnglish
      ? matchedAddress.lodNamEn || ''
      : matchedAddress.lodNamTc || '';
    displayAddress = lodName
      ? `${lodName} \n${matchedAddress.address}`
      : matchedAddress.address;
  } else {
    displayAddress = '';
  }

  return (
    <div className="service-section-container">
      <span className="vls-section-title">{t('pickupServiceDetails')}</span>

      {/* Hidden fields for time values */}
      {/* we need these Form.Item to return a valid form values when we use form.getFieldsValue */}
      <Form.Item name="colStartTime" noStyle>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="colEndTime" noStyle>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="delStartTime" noStyle>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="delEndTime" noStyle>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="delAddressId" noStyle>
        <input type="hidden" />
      </Form.Item>

      {/* Service Type Section */}
      <Row gutter={16}>
        <Col sm={12} xs={24}>
          <Form.Item
            name="serviceTypeCode"
            label={t('serviceType')}
            rules={
              isReadonly
                ? []
                : [
                  {
                    required: true,
                    message: t('pleaseSelectServiceType'),
                  },
                ]
            }
          >
            {isReadonly ? (
              <div className="readonly-field">
                {serviceOptions.find(
                  (opt) => opt.value === form.getFieldValue('serviceTypeCode'),
                )?.label || '-'}
              </div>
            ) : (
              <Select
                placeholder={t('regularLaundry')}
                options={serviceOptions}
              />
            )}
          </Form.Item>
        </Col>
      </Row>

      {/* Collection and Delivery Method Section */}
      <Form.Item
        name="pickUpMethod"
        label={t('collectionDeliveryMethod')}
        rules={
          isReadonly
            ? []
            : [
              {
                required: true,
                message: t('pleaseSelectDeliveryMethod'),
              },
            ]
        }
      >
        {isReadonly ? (
          <div className="readonly-field">
            {pickupMethods.find(
              (method) => method.pumCde === form.getFieldValue('pickUpMethod'),
            )?.pumNam || '-'}
          </div>
        ) : (
          <Radio.Group className="delivery-method-group">
            {pickupMethods.map((method) => (
              // Member portal never restricts pickup method by home quarantine — always selectable.
              <Radio key={method.pumCde} value={method.pumCde}>
                {method.pumNam}
              </Radio>
            ))}
            {!isReadonly && (
              <div className="helper-note">{t('onlyOneServiceType')}</div>
            )}
          </Radio.Group>
        )}
      </Form.Item>

      <Divider className="divider" />

      {/* Default Address Section */}
      <div className="address-section">
        <Form.Item
          name="colAddressId"
          label={t('deliveryAddress')}
          rules={
            isReadonly
              ? []
              : [
                {
                  required: true,
                  message: t('pleaseSelectAddress'),
                },
              ]
          }
        >
          {addressArray.length > 0 ? (
            <DefaultAddress
              address={displayAddress}
              onEdit={handleEditAddress}
              isReadonly={isReadonly}
            />
          ) : (
            !isReadonly && (
              <div className="add-new-address" onClick={handleAddAddress}>
                <img src={addNewAddressIcon} className="add-new-address-icon" />
                <div>{t('addAddress')}</div>
              </div>
            )
          )}
        </Form.Item>
      </div>

      {/* Collection Date and Timeslot Section */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          {/* value is schedule id */}
          <Form.Item
            name="collectionDateAndTimeslot"
            label={t('collectionDateTime')}
            rules={
              isReadonly
                ? []
                : [
                  {
                    required: true,
                    message: t('pleaseSelectCollectionDateTime'),
                  },
                ]
            }
          >
            {isReadonly ? (
              <div className="readonly-field">
                {collectionOptions.find(
                  (opt) =>
                    opt.value ===
                    form.getFieldValue('collectionDateAndTimeslot'),
                )?.label || '-'}
              </div>
            ) : (
              <Select
                placeholder={t('pleaseSelectCollectionDateTime')}
                options={collectionOptions}
              />
            )}
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item label={t('deliveryDateTime')}>
            {isReadonly ? (
              <div className="readonly-field">{deliveryDateAndTimeslot}</div>
            ) : (
              <div className="delivery-date-time">
                {deliveryDateAndTimeslot}
              </div>
            )}
          </Form.Item>
        </Col>
      </Row>

      {/* Address Management Drawer */}
      <AddressManagement
        visible={isAddressDrawerVisible}
        onClose={handleAddressDrawerClose}
        onAddressSelect={handleAddressSelect}
        selectedAddressId={collectionAddress?.addressID}
      />

      {/* New Address Drawer */}
      <DrawerFormContainer
        title={''}
        visible={isNewAddressVisible}
        onClose={handleNewAddressClose}
      >
        <NewAddress
          onClose={handleNewAddressClose}
          onSave={handleNewAddressSave}
          needTransitionAnimation={false}
          title={t('defaultAddress')}
          showDefaultCheckbox={false}
        />
      </DrawerFormContainer>
    </div>
  );
};

export default ServiceSection;